import LocalizedMessage from '../common/LocalizedMessage';
/* eslint jsx-a11y/label-has-associated-control: ["error", {"assert": "either"}] */
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Modal } from "react-bootstrap";
import service from "../../services/SupportReportService";
import { TYPE_LABELS, requestId, supportErrorMessage } from "./supportLabels";
import { renderPreview } from "./sourceCapture";
import modalManager from "./supportModalManager";
import { useTranslation } from 'react-i18next';

export default function SupportReportComposer({
  open,
  onClose,
  draft,
  setDraft,
  snapshot,
  onSuccess,
  enabled,
}) {
  const { t: tr } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [error, setError] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [uncertain, setUncertain] = useState(false);
  const command = useRef(null);
  const capturedSource = useRef(null);
  const alive = useRef(true);
  useEffect(
    () => () => {
      alive.current = false;
    },
    [],
  );
  useEffect(() => {
    if (!draft.image) {
      setImageUrl("");
      return undefined;
    }
    const url = URL.createObjectURL(draft.image);
    setImageUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [draft.image]);
  const image = (file) => {
    if (!file) return;
    if (
      !["image/png", "image/jpeg"].includes(file.type) ||
      file.size > 5 * 1024 * 1024
    ) {
      setError(tr("manage:integratedText7"));
      return;
    }
    setError("");
    setDraft((current) => ({ ...current, image: file }));
  };
  const capture = async () => {
    setCapturing(true);
    setError("");
    try {
      const result = await renderPreview(snapshot);
      if (alive.current) {
        if (result.kind === "ready") image(result.blob);
        else setError(result.reason);
      }
    } finally {
      if (alive.current) setCapturing(false);
    }
  };
  useEffect(() => {
    if (!open || !draft.source || capturedSource.current === draft.source) return;
    capturedSource.current = draft.source;
    if (!draft.image) capture();
  }, [open, draft.source]);
  const submit = async (event) => {
    event.preventDefault();
    if (busy || capturing) return;
    setBusy(true);
    setError("");
    try {
      if (!command.current) {
        const uploadId = draft.image ? await service.upload(draft.image) : null;
        if (!alive.current) return;
        command.current = {
          requestId: requestId(),
          type: draft.type,
          description: draft.description,
          source: draft.source,
          ...(uploadId ? { uploadId } : {}),
        };
      }
      const result = await service.createReport(command.current);
      if (alive.current) {
        command.current = null;
        setUncertain(false);
        onSuccess(result);
      }
    } catch (failure) {
      if (alive.current) {
        const unknown = Boolean(
          command.current && (!failure.status || failure.status >= 500),
        );
        setUncertain(unknown);
        if (!unknown) command.current = null;
        setError(
          unknown
            ? tr("manage:integratedText8")
            : supportErrorMessage(failure),
        );
      }
    } finally {
      if (alive.current) setBusy(false);
    }
  };
  const locked = busy || uncertain;
  const descriptionTooLong = [...draft.description.trim()].length > 5000;
  const sendLabel = uncertain ? tr("manage:integratedText9") : tr("manage:integratedText10");
  return (
    <Modal
      manager={modalManager}
      show={open}
      onHide={onClose}
      centered
      dialogClassName="support-composer"
      aria-labelledby="support-composer-title"
      autoFocus
      restoreFocus
    >
      <div data-support-ui="true">
        <Modal.Header closeButton closeLabel={tr("common:close")}>
          <Modal.Title id="support-composer-title">{tr("manage:integratedText11")}</Modal.Title>
        </Modal.Header>
        <form onSubmit={submit}>
          <Modal.Body>
            <p>{tr("manage:integratedText12")}</p>
            <p className="small">{tr("manage:integratedText13")}</p>
            {!enabled && (
              <p role="status" className="alert alert-info">{tr("manage:integratedText14")}</p>
            )}
            <label htmlFor="support-type">{tr("manage:integratedText15")}</label>
            <select
              id="support-type"
              className="form-control mb-3"
              value={draft.type}
              disabled={locked}
              onChange={(event) => setDraft({ ...draft, type: event.target.value })}
            >
              {Object.keys(TYPE_LABELS).map((type) => (
                <option key={type} value={type}>
                  {tr(`manage:support.type.${type}`)}
                </option>
              ))}
            </select>
            <label htmlFor="support-description">{tr("manage:description_219")}</label>
            <textarea
              id="support-description"
              className="form-control"
              rows="6"
              required
              value={draft.description}
              readOnly={locked}
              aria-describedby="support-count"
              aria-invalid={descriptionTooLong}
              onChange={(event) => setDraft({ ...draft, description: event.target.value })}
              placeholder={tr("manage:integratedText16")}
            />
            <p id="support-count" className="support-count">
              {[...draft.description].length}{tr("manage:integratedText17")}</p>
            {descriptionTooLong && <p className="text-danger" role="alert">{tr('manage:support.textTooLong')}</p>}
            <fieldset disabled={locked || capturing}>
              <legend className="h6">{tr("manage:integratedText18")}</legend>
              <p className="small">{tr("manage:integratedText19")}</p>
              {capturing && <p role="status">{tr("manage:support.preparingScreenshot")}</p>}
              {!draft.image && (
                <button
                  type="button"
                  className="btn manage-teal-button mb-2"
                  onClick={capture}
                >{tr("manage:integratedText20")}</button>
              )}
              <label className="d-block" htmlFor="support-image">{tr("manage:integratedText21")}</label>
              <input
                id="support-image"
                type="file"
                accept="image/png,image/jpeg"
                onChange={(event) => image(event.target.files[0])}
              />
              {imageUrl && (
                <div className="support-image-preview">
                  <img src={imageUrl} alt={tr("manage:integratedText22")} />
                  <button
                    type="button"
                    className="btn manage-teal-button"
                    onClick={() => setDraft({ ...draft, image: null })}
                  >{tr("manage:integratedText23")}</button>
                </div>
              )}
            </fieldset>
            {error && (
              <p className="alert alert-warning mt-3" role="alert">
                <LocalizedMessage isError value={error} />
              </p>
            )}
          </Modal.Body>
          <Modal.Footer>
            <button
              type="button"
              className="btn manage-teal-button"
              onClick={onClose}
            >{tr("common:cancel")}</button>
            <button
              type="submit"
              className="btn manage-button manage-red-button"
              disabled={
                busy ||
                capturing ||
                (!enabled && !uncertain) ||
                !draft.description.trim() ||
                descriptionTooLong
              }
            >
              {busy ? tr("manage:integratedText24") : sendLabel}
            </button>
          </Modal.Footer>
        </form>
      </div>
    </Modal>
  );
}
SupportReportComposer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  draft: PropTypes.object.isRequired,
  setDraft: PropTypes.func.isRequired,
  snapshot: PropTypes.object,
  onSuccess: PropTypes.func.isRequired,
  enabled: PropTypes.bool.isRequired,
};
SupportReportComposer.defaultProps = { snapshot: null };
